import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export interface TooltipProps {
  content: ReactNode
  children: ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
  delay?: number
}

const GAP = 6
const EDGE = 8
const FLIP = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' } as const

/** Hover or focus to see; keyboard users get it too. Drawn on the body (fixed), so a header at the
 * top of the screen or a parent with overflow hidden never cuts it off; flips side and stays on screen. */
export function Tooltip({ content, children, position = 'top', delay = 250 }: TooltipProps) {
  const [show, setShow] = useState(false)
  const [at, setAt] = useState<{ top: number; left: number } | null>(null)
  const timer = useRef<number | null>(null)
  const anchor = useRef<HTMLSpanElement>(null)
  const tip = useRef<HTMLSpanElement>(null)
  const id = useId()
  const open = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setShow(true), delay)
  }
  const close = () => {
    if (timer.current) clearTimeout(timer.current)
    setShow(false)
    setAt(null)
  }

  useLayoutEffect(() => {
    if (!show || !anchor.current || !tip.current) return
    const a = anchor.current.getBoundingClientRect()
    const t = tip.current.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const fits = (p: keyof typeof FLIP) =>
      p === 'top' ? a.top - GAP - t.height >= EDGE
        : p === 'bottom' ? a.bottom + GAP + t.height <= vh - EDGE
          : p === 'left' ? a.left - GAP - t.width >= EDGE
            : a.right + GAP + t.width <= vw - EDGE
    const side = fits(position) || !fits(FLIP[position]) ? position : FLIP[position]
    let top = side === 'top' ? a.top - GAP - t.height : side === 'bottom' ? a.bottom + GAP : a.top + a.height / 2 - t.height / 2
    let left = side === 'left' ? a.left - GAP - t.width : side === 'right' ? a.right + GAP : a.left + a.width / 2 - t.width / 2
    left = Math.min(Math.max(left, EDGE), vw - EDGE - t.width)
    top = Math.min(Math.max(top, EDGE), vh - EDGE - t.height)
    setAt({ top, left })
  }, [show, position, content])

  return (
    <span
      ref={anchor}
      // a disabled button gets no mouse events, so let the hover reach this wrapper
      className="relative inline-flex [&_:disabled]:pointer-events-none"
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
      aria-describedby={show ? id : undefined}
    >
      {children}
      {show &&
        createPortal(
          <span
            ref={tip}
            role="tooltip"
            id={id}
            style={{ top: at?.top ?? 0, left: at?.left ?? 0, visibility: at ? 'visible' : 'hidden' }}
            className="pointer-events-none fixed z-[300] w-max max-w-xs rounded bg-slate-900 px-2 py-1 text-2xs font-medium text-white animate-fade-in dark:bg-slate-700"
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  )
}
